import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/authFetch";
import { supabase } from "@/lib/supabase";
import {
  X, Camera, Upload, RotateCcw, Check, AlertCircle, Loader2,
  Package, FileText, ShoppingCart, Scan, ChevronRight, Eye
} from "lucide-react";
import { useLocation } from "wouter";

/* ── Types ── */
interface QuotaInfo { used: number; limit: number; remaining: number; reset_at: string; override_active: boolean; }
interface ScanMatch { product_id: string; confidence: number; reasoning: string; distinctive_features_matched?: string[]; product?: any; }
interface ScanResult {
  detection_method: "sku_ocr" | "ai_vision" | "qr_code";
  matches: ScanMatch[];
  no_match_likely: boolean;
  image_quality_issues: string | null;
  scan_id: string;
}

type Stage = "filters" | "camera" | "loading" | "results" | "action";

const ROBOT_CHIPS = ["CC1", "MT1", "BellaBot", "KettyBot", "FlashBot", "SwiftBot", "PuduBot 2", "PUDU T300"];
const TYPE_CHIPS = [
  { value: "robot", label: "Robot" },
  { value: "accessoire", label: "Accessoire" },
  { value: "consommable", label: "Consommable" },
  { value: "piece_detachee", label: "Pièce SAV" },
];

const LOADING_MESSAGES = [
  "Envoi de la photo...",
  "Analyse en cours...",
  "Identification du produit...",
];

function confidenceColor(score: number) {
  if (score >= 0.85) return "bg-green-500";
  if (score >= 0.60) return "bg-yellow-500";
  return "bg-red-500";
}
function confidenceLabel(score: number) {
  if (score >= 0.85) return "text-green-600 dark:text-green-400";
  if (score >= 0.60) return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
}

/* ── Compress image client-side ── */
async function compressImage(file: File, maxDim = 1280): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => resolve(b!), "image/jpeg", 0.85);
    };
    img.src = url;
  });
}

interface Props { onClose: () => void; }

export default function ProductScanner({ onClose }: Props) {
  const [stage, setStage] = useState<Stage>("filters");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [robotFilters, setRobotFilters] = useState<string[]>([]);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<ScanMatch | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { toast } = useToast();
  const [, navigate] = useLocation();

  /* ── Fetch quota on mount ── */
  useEffect(() => {
    authFetch("/api/products/scan/quota")
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setQuota(d))
      .catch(() => {});
  }, []);

  /* ── Loading message rotation ── */
  useEffect(() => {
    if (stage !== "loading") { if (loadingTimerRef.current) clearInterval(loadingTimerRef.current); return; }
    setLoadingMsgIdx(0);
    loadingTimerRef.current = setInterval(() => {
      setLoadingMsgIdx(i => Math.min(i + 1, LOADING_MESSAGES.length - 1));
    }, 2500);
    return () => { if (loadingTimerRef.current) clearInterval(loadingTimerRef.current); };
  }, [stage]);

  /* ── File selection ── */
  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
    setStage("camera");
  };

  const triggerCapture = (capture?: "environment") => {
    if (!fileInputRef.current) return;
    fileInputRef.current.setAttribute("capture", capture || "");
    fileInputRef.current.click();
  };

  /* ── Upload + Scan ── */
  const handleAnalyze = useCallback(async () => {
    if (!selectedFile) return;
    setStage("loading");

    try {
      // Compress
      const blob = await compressImage(selectedFile);

      // Send directly to backend as multipart (backend uploads to storage via service role)
      const filters: any = {};
      if (typeFilter) filters.type = typeFilter;
      if (robotFilters.length > 0) filters.compatible_robots = robotFilters;

      const formData = new FormData();
      formData.append("file", new File([blob], "scan.jpg", { type: "image/jpeg" }));
      formData.append("filters", JSON.stringify(filters));

      const res = await authFetch("/api/products/scan", {
        method: "POST",
        body: formData,
      });

      if (res.status === 429) {
        const data = await res.json();
        toast({
          variant: "destructive",
          title: "Quota journalier atteint",
          description: data.message || "Limite de scans atteinte pour aujourd'hui.",
        });
        setStage("filters");
        return;
      }
      if (res.status === 400) {
        const data = await res.json();
        toast({ variant: "destructive", title: "Filtres trop larges", description: data.message });
        setStage("filters");
        return;
      }
      if (!res.ok) throw new Error(await res.text());

      const result: ScanResult = await res.json();
      setScanResult(result);
      // Refresh quota
      authFetch("/api/products/scan/quota").then(r => r.ok ? r.json() : null).then(d => d && setQuota(d)).catch(() => {});
      setStage("results");
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur d'analyse", description: err.message || "Réessayez." });
      setStage("camera");
    }
  }, [selectedFile, typeFilter, robotFilters, toast]);

  /* ── Post-validation action ── */
  const handleAction = async (action: "view_product" | "create_sav_ticket" | "create_order" | "none") => {
    if (!scanResult || !selectedMatch) return;
    await authFetch(`/api/products/scan/${scanResult.scan_id}/action`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ selected_product_id: selectedMatch.product_id, action }),
    }).catch(() => {});

    if (action === "view_product") {
      onClose();
      navigate(`/products?open=${selectedMatch.product_id}`);
    } else if (action === "create_sav_ticket") {
      onClose();
      navigate(`/tickets?newTicket=1&productId=${selectedMatch.product_id}`);
    } else if (action === "create_order") {
      onClose();
      navigate(`/commandes?newOrder=1&productId=${selectedMatch.product_id}`);
    } else {
      onClose();
    }
  };

  const handleNoMatch = async () => {
    if (!scanResult) return;
    await authFetch(`/api/products/scan/${scanResult.scan_id}/action`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ selected_product_id: null, action: "none", no_match: true }),
    }).catch(() => {});
    onClose();
  };

  const reset = () => {
    setPreview(null);
    setSelectedFile(null);
    setScanResult(null);
    setSelectedMatch(null);
    setStage("filters");
  };

  /* ── Quota badge ── */
  const QuotaBadge = () => {
    if (!quota) return null;
    const pct = quota.used / quota.limit;
    const color = pct >= 1 ? "text-red-500" : pct >= 0.7 ? "text-orange-500" : "text-green-500";
    const bg = pct >= 1 ? "bg-red-50 dark:bg-red-950/30 border-red-200" : pct >= 0.7 ? "bg-orange-50 dark:bg-orange-950/30 border-orange-200" : "bg-green-50 dark:bg-green-950/30 border-green-200";
    return (
      <div title={`Reset à minuit UTC · ${quota.override_active ? "Override actif" : ""}`}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${color} ${bg}`}>
        <Scan className="w-3.5 h-3.5" />
        {quota.used}/{quota.limit} scans
      </div>
    );
  };

  /* ═══════════════════════════════════════════
   * RENDER
   * ═══════════════════════════════════════════ */
  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Scan className="w-5 h-5 text-primary" />
            <span className="font-semibold">Scanner un produit</span>
          </div>
          <div className="flex items-center gap-2">
            <QuotaBadge />
            <Button variant="ghost" size="icon" onClick={onClose} className="w-8 h-8" data-testid="button-scanner-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* ── SCREEN 1: Filters ── */}
        {stage === "filters" && (
          <div className="flex-1 flex flex-col gap-5 p-5">
            <div>
              <p className="text-sm text-muted-foreground mb-3">Sélectionne les filtres pour améliorer la précision</p>

              {/* Quota atteint */}
              {quota && quota.remaining === 0 && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">
                    Tu as utilisé tes {quota.limit} scans du jour.
                  </p>
                  <p className="text-xs text-red-600/70 dark:text-red-500/70 mt-1">
                    Reset à minuit UTC. Si besoin urgent, demande un override à Jonathan.
                  </p>
                </div>
              )}

              {/* Type chips */}
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Type de produit</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {TYPE_CHIPS.map(t => (
                  <button key={t.value} type="button"
                    onClick={() => setTypeFilter(typeFilter === t.value ? null : t.value)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${typeFilter === t.value ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}
                    data-testid={`chip-type-${t.value}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Robot chips */}
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Robot compatible</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {ROBOT_CHIPS.map(r => (
                  <button key={r} type="button"
                    onClick={() => setRobotFilters(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r])}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${robotFilters.includes(r) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}
                    data-testid={`chip-robot-${r.replace(/\s/g, "-")}`}>
                    {r}
                  </button>
                ))}
              </div>

              {(typeFilter || robotFilters.length > 0) && (
                <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" />
                  Plus tu précises, plus la suggestion sera fiable
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div className="space-y-2 mt-auto">
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="hidden"
                onChange={handleFileSelected} data-testid="input-photo-file" />
              <Button className="w-full gap-2" size="lg"
                disabled={quota?.remaining === 0}
                onClick={() => triggerCapture("environment")}
                data-testid="button-take-photo">
                <Camera className="w-5 h-5" />
                Prendre une photo
              </Button>
              <Button variant="outline" className="w-full gap-2"
                disabled={quota?.remaining === 0}
                onClick={() => { if (fileInputRef.current) { fileInputRef.current.removeAttribute("capture"); fileInputRef.current.click(); } }}
                data-testid="button-import-photo">
                <Upload className="w-4 h-4" />
                Importer depuis la galerie
              </Button>
            </div>
          </div>
        )}

        {/* ── SCREEN 2: Camera preview ── */}
        {stage === "camera" && preview && (
          <div className="flex-1 flex flex-col gap-4 p-5">
            <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
              <img src={preview} alt="Aperçu" className="w-full h-full object-contain" />
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                Centre l'objet · lumière correcte · fond uni si possible
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="gap-2" onClick={() => { setPreview(null); setSelectedFile(null); triggerCapture("environment"); }}
                data-testid="button-retake">
                <RotateCcw className="w-4 h-4" />
                Reprendre
              </Button>
              <Button className="gap-2" onClick={handleAnalyze} data-testid="button-analyze">
                <Scan className="w-4 h-4" />
                Analyser
              </Button>
            </div>
          </div>
        )}

        {/* ── SCREEN 3: Loading ── */}
        {stage === "loading" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8">
            <div className="relative">
              {preview && <img src={preview} alt="" className="w-24 h-24 rounded-xl object-cover opacity-40" />}
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground animate-pulse">{LOADING_MESSAGES[loadingMsgIdx]}</p>
          </div>
        )}

        {/* ── SCREEN 4: Results ── */}
        {stage === "results" && scanResult && (
          <div className="flex-1 flex flex-col gap-4 p-5">
            {/* Quality issue alert */}
            {scanResult.image_quality_issues && (
              <div className="bg-orange-50 dark:bg-orange-950/30 border border-orange-200 rounded-lg p-3 flex gap-2">
                <AlertCircle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                <p className="text-xs text-orange-700 dark:text-orange-400">{scanResult.image_quality_issues}</p>
              </div>
            )}

            {/* Detection method badge */}
            {scanResult.detection_method === "sku_ocr" && (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 w-fit gap-1">
                <Check className="w-3 h-3" /> SKU détecté — Confiance 100%
              </Badge>
            )}

            {/* No match */}
            {scanResult.no_match_likely && scanResult.matches.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <div className="w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <AlertCircle className="w-7 h-7 text-orange-500" />
                </div>
                <p className="font-medium">Aucun produit correspondant</p>
                <p className="text-sm text-muted-foreground">Essaie d'élargir les filtres ou de reprendre la photo avec plus de lumière.</p>
              </div>
            )}

            {/* Matches */}
            {scanResult.matches.map((match, i) => {
              const p = match.product;
              return (
                <div key={match.product_id}
                  className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedMatch?.product_id === match.product_id ? "border-primary ring-2 ring-primary/20 bg-primary/5" : "border-border hover:border-primary/50"}`}
                  onClick={() => setSelectedMatch(match)}
                  data-testid={`result-card-${i}`}>
                  <div className="flex gap-3">
                    {p?.imageUrl ? (
                      <img src={p.imageUrl} alt="" className="w-16 h-16 object-contain rounded-lg border bg-muted shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg border bg-muted flex items-center justify-center shrink-0">
                        <Package className="w-8 h-8 text-muted-foreground/50" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{p?.nomFr || "Produit inconnu"}</p>
                      {p?.sku && <p className="text-xs text-muted-foreground font-mono">{p.sku}</p>}
                      {/* Confidence bar */}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className={`h-full rounded-full ${confidenceColor(match.confidence)}`}
                            style={{ width: `${Math.round(match.confidence * 100)}%` }} />
                        </div>
                        <span className={`text-xs font-semibold ${confidenceLabel(match.confidence)}`}>
                          {Math.round(match.confidence * 100)}%
                        </span>
                      </div>
                      {/* Features matched */}
                      {match.distinctive_features_matched && match.distinctive_features_matched.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {match.distinctive_features_matched.slice(0, 3).map((f, fi) => (
                            <span key={fi} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{f}</span>
                          ))}
                        </div>
                      )}
                      {/* Reasoning */}
                      <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{match.reasoning}</p>
                    </div>
                  </div>
                  {selectedMatch?.product_id === match.product_id && (
                    <Button size="sm" className="w-full mt-3 gap-1.5" data-testid={`button-select-match-${i}`}
                      onClick={e => { e.stopPropagation(); setStage("action"); }}>
                      <Check className="w-4 h-4" />
                      C'est celui-ci ✓
                    </Button>
                  )}
                </div>
              );
            })}

            {/* Footer actions */}
            <div className="flex gap-2 pt-2 mt-auto">
              <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={reset}
                data-testid="button-rescan">
                <RotateCcw className="w-3.5 h-3.5" />
                Nouvelle photo
              </Button>
              <Button variant="ghost" size="sm" className="ml-auto gap-1.5 text-muted-foreground" onClick={handleNoMatch}
                data-testid="button-no-match">
                Aucun ne correspond
              </Button>
            </div>
            {quota && (
              <p className="text-xs text-muted-foreground text-center">Il te reste {quota.remaining - 1} scans aujourd'hui</p>
            )}
          </div>
        )}

        {/* ── SCREEN 5: Action ── */}
        {stage === "action" && selectedMatch && (
          <div className="flex-1 flex flex-col gap-5 p-5">
            {/* Confirmation */}
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <p className="font-semibold">Produit identifié !</p>
            </div>

            {/* Product summary */}
            <div className="border rounded-xl p-4 flex gap-3 bg-muted/30">
              {selectedMatch.product?.imageUrl ? (
                <img src={selectedMatch.product.imageUrl} alt="" className="w-14 h-14 object-contain rounded-lg border bg-white shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-lg border bg-muted flex items-center justify-center shrink-0">
                  <Package className="w-7 h-7 text-muted-foreground/50" />
                </div>
              )}
              <div>
                <p className="font-medium">{selectedMatch.product?.nomFr}</p>
                <p className="text-xs text-muted-foreground font-mono">{selectedMatch.product?.sku}</p>
                <p className="text-xs text-muted-foreground mt-1">{Math.round(selectedMatch.confidence * 100)}% de confiance</p>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <Button className="w-full justify-between gap-2" variant="outline"
                onClick={() => handleAction("view_product")} data-testid="action-view-product">
                <div className="flex items-center gap-2"><Eye className="w-4 h-4" /> Voir la fiche complète</div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Button>
              <Button className="w-full justify-between gap-2" variant="outline"
                onClick={() => handleAction("create_sav_ticket")} data-testid="action-create-ticket">
                <div className="flex items-center gap-2"><FileText className="w-4 h-4" /> Créer un ticket SAV</div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Button>
              <Button className="w-full justify-between gap-2" variant="outline"
                onClick={() => handleAction("create_order")} data-testid="action-create-order">
                <div className="flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Commander / Réapprovisionner</div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Button>
            </div>

            <Button variant="ghost" className="text-muted-foreground" onClick={() => handleAction("none")}
              data-testid="action-done">
              Terminer sans action
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
